import React, { useCallback, useEffect, useMemo, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  CalendarDays,
  BadgeIndianRupee,
  CircleCheck,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  Info,
  Mail,
  MapPin,
  Menu,
  Phone,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
  Waves,
  X,
} from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASSETS = {
  logo: "/assets/raaj-logo.jpg",
  room: "/assets/raaj-room.jpg",
  exterior: "/assets/raaj-resort-exterior.jpg",
  propertyExteriorGarden: "/assets/raaj-property-corridor.webp",
  propertyCorridor: "/assets/raaj-property-exterior-garden.webp",
  weddingLawn: "/assets/raaj-wedding-lawn-new-1.webp",
  weddingLawnAerial: "/assets/raaj-wedding-lawn-new-2.webp",
  banquetHall: "/assets/raaj-ac-banquet-final.jpg",
  kidsPlayArea: "/assets/raaj-kids-play-area-final.jpg",
  spaOne: "/assets/raaj-spa-final-1.jpg",
  spaTwo: "/assets/raaj-spa-final-2.jpg",
  spaThree: "/assets/raaj-spa-final-3.jpg",
  roomCorridor: "/assets/raaj-room-corridor.jpg",
  poolArea: "/assets/raaj-games-room.jpg",
  gamesRoom: "/assets/raaj-pool-area.jpg",
  restaurantDining: "/assets/raaj-restaurant-dining.jpg",
  restaurantCabanas: "/assets/raaj-play-area.jpg",
  deluxeRoom: "/assets/raaj-deluxe-room-final.jpg",
  deluxeRoomWithShowerOne: "/assets/raaj-deluxe-room-with-shower-2.webp",
  deluxeRoomWithShowerTwo: "/assets/raaj-deluxe-room-with-shower-1.webp",
  suiteBathtub: "/assets/raaj-suite-bathtub-final.jpg",
  suiteRoomWithBathtubOne: "/assets/raaj-suite-room-with-bathtub-1.webp",
  suiteRoomWithBathtubTwo: "/assets/raaj-suite-room-with-bathtub-2.webp",
  suiteRoomWithBathtubThree: "/assets/raaj-suite-room-with-bathtub-3.webp",
  suiteRoomWithBathtubFour: "/assets/raaj-suite-room-with-bathtub-4.webp",
  quadRoomsGeneral: "/assets/raaj-quad-rooms-general-final.jpg",
  quadRoomNo1One: "/assets/raaj-quad-room-no-1-1.webp",
  quadRoomNo1Two: "/assets/raaj-quad-room-no-1-2.webp",
  quadRoomNo2One: "/assets/raaj-quad-room-no-2-1.webp",
  quadRoomNo2Two: "/assets/raaj-quad-room-no-2-2.webp",
  quadRoomNo2Three: "/assets/raaj-quad-room-no-2-3.webp",
  quadRoomNo2Four: "/assets/raaj-quad-room-no-2-4.webp",
  quadRoomNo3One: "/assets/raaj-quad-room-no-3-1.webp",
  quadRoomNo3Two: "/assets/raaj-quad-room-no-3-2.webp",
  parkingArea: "/assets/raaj-parking-area-1.webp",
  weddingPackagePoster: "/assets/raaj-wedding-package-poster.webp",
};

const BUSINESS = {
  name: "Raaj Resort",
  address: "Usarli (Budruk), Near Ritghar, Panvel, Navi Mumbai, Maharashtra – 410206, India",
  phones: ["+91 98193 44222"],
  email: "hotelraajrestaurant@gmail.com",
  whatsappNumber: "919819344222",
};

const navLinks = [
  ["Home", "home"],
  ["About", "about"],
  ["Rooms", "rooms"],
  ["Group Packages", "packages"],
  ["Weddings & Events", "weddings"],
  ["Restaurant", "restaurant"],
  ["Gallery", "gallery"],
  ["Contact", "contact"],
];

const rooms = [
  {
    name: "Deluxe Room With Shower",
    slug: "deluxe-room-with-shower",
    persons: "For 2 persons",
    image: ASSETS.deluxeRoomWithShowerOne,
    gallery: [ASSETS.deluxeRoomWithShowerOne, ASSETS.deluxeRoomWithShowerTwo, ASSETS.deluxeRoom],
    photoNote: "Deluxe Room With Shower",
    prices: { weekday: "₹2,200", weekend: "₹2,600", sixWeekday: "₹1,500", sixWeekend: "₹1,800" },
  },
  {
    name: "Suite Room with Bathtub",
    slug: "suite-room-with-bathtub",
    persons: "For 2 persons",
    image: ASSETS.suiteRoomWithBathtubThree,
    gallery: [ASSETS.suiteRoomWithBathtubThree, ASSETS.suiteRoomWithBathtubOne, ASSETS.suiteRoomWithBathtubTwo, ASSETS.suiteRoomWithBathtubFour],
    features: ["Bathtub shown"],
    photoNote: "Suite Room with Bathtub",
    prices: { weekday: "₹3,200", weekend: "₹3,600", sixWeekday: "₹2,500", sixWeekend: "₹2,800" },
  },
  {
    name: "Quad Room No. 1",
    slug: "quad-room-no-1",
    persons: "For 6 persons",
    image: ASSETS.quadRoomNo1One,
    gallery: [ASSETS.quadRoomNo1One, ASSETS.quadRoomNo1Two],
    photoNote: "Quad Room No. 1",
    prices: { weekday: "₹7,000", weekend: "₹8,000", sixWeekday: "₹4,000", sixWeekend: "₹5,000" },
  },
  {
    name: "Quad Room No. 2",
    slug: "quad-room-no-2",
    persons: "For 12 persons",
    image: ASSETS.quadRoomNo2One,
    gallery: [ASSETS.quadRoomNo2One, ASSETS.quadRoomNo2Two, ASSETS.quadRoomNo2Three, ASSETS.quadRoomNo2Four],
    photoNote: "Quad Room No. 2",
    prices: { weekday: "₹8,000", weekend: "₹12,000", sixWeekday: "₹6,000", sixWeekend: "₹6,000" },
  },
  {
    name: "Quad Room No. 3",
    slug: "quad-room-no-3",
    persons: "For 4 persons",
    image: ASSETS.quadRoomNo3One,
    gallery: [ASSETS.quadRoomNo3One, ASSETS.quadRoomNo3Two],
    photoNote: "Quad Room No. 3",
    prices: { weekday: "₹4,000", weekend: "₹5,000", sixWeekday: "₹3,000", sixWeekend: "₹4,000" },
  },
];

const tariffNotes = [
  "Breakfast is not included in the room tariff.",
  "Check-in: 01:00 PM",
  "Check-out: 11:00 AM",
  "Extra hours: ₹300 per hour.",
  "Extra person charge: ₹500 per person.",
  "Booking amount is non-refundable.",
  "Outside food and aerated drinks are strictly prohibited inside the resort premises.",
];

const packageData = [
  {
    id: "01",
    title: "Group Per Person Package 01",
    price: "₹3,000 per person",
    min: "Minimum 10 pax required",
    testId: "group-package-01-card",
    includes: [
      "Lunch / Dinner menu",
      "Dal Fry",
      "Curd",
      "Vegetable Dish (Any 2)",
      "Gulab Jamun",
      "Chicken Gravy / Chicken Sukha",
      "Chapathi / Bhakri",
      "Rice",
      "Salad & Pickle",
      "Hi Tea",
      "Snacks: Biscuits",
      "Tea & Coffee",
      "Breakfast",
      "Poha / Upma / Misal Pav (Any Two)",
      "Tea & Coffee",
      "Rooms as per package allocation",
    ],
    allocation: ["Quad Room 1: 6 pax", "Quad Room 3: 4 pax", "Deluxe Room: 2 pax"],
  },
  {
    id: "02",
    title: "Group Per Person Package 02",
    price: "₹2,500 per person",
    min: "Minimum 12 pax required",
    testId: "group-package-02-card",
    includes: [
      "Lunch / Dinner",
      "Dal Fry",
      "Vegetable Dish (Any 2)",
      "Gulab Jamun",
      "Chicken Gravy / Chicken Sukha",
      "Chapathi / Bhakri",
      "Rice",
      "Salad & Pickle",
      "Hi Tea",
      "Biscuits",
      "Tea & Coffee",
      "Breakfast",
      "Poha / Upma / Misal Pav (Any Two)",
      "Tea & Coffee",
      "Rooms as per package allocation",
    ],
    allocation: ["Quad Room 1: 8 pax", "Quad Room 2: 16 pax", "Quad Room 3: 6 pax"],
  },
];

const mealTimings = [
  ["Breakfast", "9:00 AM – 10:00 AM"],
  ["Lunch", "1:00 PM – 2:30 PM"],
  ["Hi Tea", "5:00 PM – 6:00 PM"],
  ["Dinner", "9:00 PM – 10:30 PM"],
];

const eventTypes = ["Wedding", "Birthday", "Corporate Event", "Family Function", "Group Booking", "Other Event"];
const enquiryTypes = ["Room Booking", "Group Package", "Wedding", "Event", "Restaurant", "General Enquiry"];
const roomTypes = ["Not decided", ...rooms.map((room) => room.name)];
const statuses = ["New", "Contacted", "Follow-up", "Confirmed", "Closed"];

const galleryImages = [
  { src: ASSETS.weddingLawn, title: "Wedding Lawn at Raaj Resort", category: "Wedding Lawn" },
  { src: ASSETS.weddingLawnAerial, title: "Wedding Lawn aerial view at Raaj Resort", category: "Wedding Lawn" },
  { src: ASSETS.banquetHall, title: "A/C Banquet Hall at Raaj Resort", category: "Events" },
  { src: ASSETS.kidsPlayArea, title: "Kids Play Area at Raaj Resort", category: "Family" },
  { src: ASSETS.spaOne, title: "Spa & Wellness treatment room at Raaj Resort", category: "Wellness" },
  { src: ASSETS.spaTwo, title: "Spa & Wellness reception photograph supplied by Raaj Resort", category: "Wellness" },
  { src: ASSETS.spaThree, title: "Spa & Wellness treatment room photograph supplied by Raaj Resort", category: "Wellness" },
  { src: ASSETS.exterior, title: "Raaj Resort exterior and resort setting", category: "Resort" },
  { src: ASSETS.deluxeRoomWithShowerOne, title: "Deluxe Room With Shower photograph supplied by Raaj Resort", category: "Rooms" },
  { src: ASSETS.suiteRoomWithBathtubThree, title: "Suite Room with Bathtub photograph supplied by Raaj Resort", category: "Rooms" },
  { src: ASSETS.quadRoomsGeneral, title: "General Quad Rooms visual supplied by Raaj Resort", category: "Rooms" },
  { src: ASSETS.roomCorridor, title: "Raaj Resort room corridor photograph", category: "Rooms" },
  { src: ASSETS.restaurantDining, title: "Pavika Restaurant & Bar dining space", category: "Dining" },
  { src: ASSETS.restaurantCabanas, title: "Raaj Resort dining and resort experience photograph", category: "Dining" },
  { src: ASSETS.poolArea, title: "Raaj Resort outdoor resort experience photograph", category: "Resort" },
  { src: ASSETS.gamesRoom, title: "Raaj Resort indoor resort photograph", category: "Resort" },
];

const spaPhotos = [
  { src: ASSETS.spaOne, alt: "Spa & Wellness treatment room at Raaj Resort" },
  { src: ASSETS.spaTwo, alt: "Spa & Wellness reception photograph supplied by Raaj Resort" },
  { src: ASSETS.spaThree, alt: "Spa & Wellness treatment room photograph supplied by Raaj Resort" },
];

const weddingDecorationPhotos = Array.from({ length: 17 }, (_, index) => ({
  src: `/assets/raaj-wedding-decoration-${index + 1}.webp`,
  alt: `Wedding Decoration photograph ${index + 1} supplied by Raaj Resort`,
}));


const caterersBuffetPhotos = Array.from({ length: 4 }, (_, index) => ({
  src: `/assets/raaj-caterers-buffet-${index + 1}.webp`,
  alt: `Caterers & Buffet Setup photograph ${index + 1} supplied by Raaj Resort`,
}));

const parkingAreaPhotos = [
  { src: ASSETS.parkingArea, alt: "Parking Area photograph supplied by Raaj Resort" },
];

const weddingPackageInfo = [
  "Perfect Wedding Destination at Raaj Resort",
  "Accommodation facilities available for weddings",
  "Stay capacity: Up to 100 guests",
  "Spacious Wedding Lawn: Capacity for 800 to 1000 people",
  "Ample car parking available",
  "Swimming pool facility",
  "Indoor games available",
  "Decoration & Catering Services available at affordable prices",
  "Complete Venue Package: ₹3 Lakhs for 2 Days",
  "WhatsApp / Booking: +91 98193 44222",
  "Additional booking contact: +91 98193 44222",
  "Location: Usarli (Budruk), Near Ritghar, Panvel, Navi Mumbai, Maharashtra - 410206, India",
];


const rainDancePhotos = [
  { src: "/assets/raaj-rain-dance-1.webp", alt: "Rain Dance photograph 1 supplied by Raaj Resort" },
  { src: "/assets/raaj-rain-dance-2.webp", alt: "Rain Dance photograph 2 supplied by Raaj Resort" },
];

const freeAccessItems = [
  "Swimming Pool",
  "Kids’ Games",
  "Billiards / Pool Table",
  "Table Tennis",
  "Carrom",
  "Chess",
  "Badminton",
];

const chargeableItems = ["Spa & Massage", "Restaurant & Bar", "Rain Dance"];

const importantNotes = [
  "In case of an electricity power cut, the resort has inverter power backup. During a power cut, air-conditioning service in the rooms will not be available.",
  "For wedding functions, both generator and inverter power backup will be available.",
  "Bringing outside food and alcohol into the resort premises is strictly prohibited.",
  "Mineral water included in the package will be available only during Breakfast, Lunch, Hi-Tea and Dinner.",
  "At the time of check-in, guests must clear all outstanding or due payments before checking in.",
];

const directionsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(BUSINESS.address)}`;

function whatsappLink() {
  return `https://wa.me/${BUSINESS.whatsappNumber}`;
}

function scrollToSection(id) {
  const target = document.getElementById(id);
  if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
}

function SectionHeading({ eyebrow, title, children }) {
  return (
    <div className="section-heading">
      <p className="eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      {children && <p className="section-copy">{children}</p>}
    </div>
  );
}

function ButtonLink({ children, className = "", ...props }) {
  return (
    <a className={`btn ${className}`} {...props}>
      {children}
    </a>
  );
}

function Navigation() {
  const [open, setOpen] = useState(false);
  const go = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <header className="site-nav" data-testid="site-navigation">
      <Link to="/" className="brand-mark" data-testid="nav-logo-link" onClick={() => setTimeout(() => scrollToSection("home"), 50)}>
        <img src={ASSETS.logo} alt="Raaj Resort official logo" />
        <span>Raaj Resort</span>
      </Link>
      <nav className="desktop-nav" aria-label="Primary navigation">
        {navLinks.map(([label, id]) => (
          <button key={id} onClick={() => go(id)} data-testid={`nav-${id}-link`}>
            {label}
          </button>
        ))}
      </nav>
      <div className="nav-actions">
        <button className="btn btn-primary" onClick={() => go("contact")} data-testid="nav-enquire-button">
          Book / Enquire Now
        </button>
        <button className="menu-btn" onClick={() => setOpen(true)} aria-label="Open mobile navigation" data-testid="mobile-menu-open-button">
          <Menu size={22} />
        </button>
      </div>
      {open && (
        <div className="mobile-sheet" data-testid="mobile-navigation-sheet">
          <div className="mobile-sheet-panel">
            <div className="sheet-head">
              <img src={ASSETS.logo} alt="Raaj Resort official logo" />
              <button onClick={() => setOpen(false)} aria-label="Close mobile navigation" data-testid="mobile-menu-close-button">
                <X size={22} />
              </button>
            </div>
            {navLinks.map(([label, id]) => (
              <button key={id} onClick={() => go(id)} data-testid={`mobile-nav-${id}-link`}>
                {label}
              </button>
            ))}
            <button className="btn btn-primary full" onClick={() => go("contact")} data-testid="mobile-nav-enquire-button">
              Book / Enquire Now
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section id="home" className="hero-section" data-testid="hero-section">
      <div className="hero-bg" style={{ backgroundImage: `url(${ASSETS.exterior})` }} aria-hidden="true" />
      <div className="hero-overlay" />
      <div className="container hero-grid">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75 }} className="hero-content">
          <p className="eyebrow gold">3-Star Property • 4.7 Google Rating</p>
          <h1>Experience Comfort. Celebrate Moments.</h1>
          <p>
            Raaj Resort – Your destination for stays, celebrations, weddings, groups and memorable experiences near Panvel.
          </p>
          <div className="proof-badges" data-testid="hero-proof-badges">
            <span>3-Star Property</span>
            <span>4.7 Google Rating</span>
          </div>
          <div className="hero-ctas">
            <button className="btn btn-accent" onClick={() => scrollToSection("contact")} data-testid="hero-enquire-now-button">
              Book / Enquire Now
            </button>
            <button className="btn btn-light" onClick={() => scrollToSection("rooms")} data-testid="hero-explore-rooms-button">
              Explore Rooms
            </button>
          </div>
          <div className="hero-contact-row">
            <ButtonLink href={whatsappLink()} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-button">
              WhatsApp
            </ButtonLink>
            <ButtonLink href="tel:+919819344222" data-testid="hero-call-button">
              Call +91 98193 44222
            </ButtonLink>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.18 }} className="hero-card">
          <p className="eyebrow">Enquiry-first bookings</p>
          <h3>A Perfect Setting for Weddings & Celebrations</h3>
          <p>From intimate family celebrations to weddings and special events, Raaj Resort offers a comfortable setting with a Wedding Lawn, A/C Banquet Hall, rooms and Pavika Restaurant & Bar.</p>
          <div className="mini-list">
            <span>Rooms</span>
            <span>Group Packages</span>
            <span>Wedding Lawn</span>
            <span>A/C Banquet Hall</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function About() {
  const items = ["Comfortable stays", "Family getaways", "Group stays", "Weddings", "Celebrations", "Events", "Restaurant & dining", "Banquet functions"];
  return (
    <section id="about" className="section" data-testid="about-section">
      <div className="container about-grid">
        <div className="about-media about-collage">
          <img src={ASSETS.propertyExteriorGarden} alt="Raaj Resort property exterior and garden" />
          <div className="about-small-grid">
            <img src={ASSETS.propertyCorridor} alt="Raaj Resort property corridor" />
            <img src={ASSETS.exterior} alt="Raaj Resort property photograph" />
          </div>
        </div>
        <div>
          <SectionHeading eyebrow="About Raaj Resort" title="A 3-star property for stays, celebrations and group moments near Panvel.">
            With a confirmed 4.7 Google Rating, Raaj Resort brings together rooms, event spaces, dining and group package options for guests planning a stay, family getaway, celebration, wedding, banquet function or restaurant enquiry.
          </SectionHeading>
          <div className="pill-grid">
            {items.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}


function PreferredRoomEnquiry({ room }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    check_in_date: "",
    check_out_date: "",
    number_of_guests: "",
    full_name: "",
    mobile_number: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        full_name: form.full_name,
        mobile_number: form.mobile_number,
        email: null,
        check_in_date: form.check_in_date,
        check_out_date: form.check_out_date,
        number_of_guests: Number(form.number_of_guests),
        room_type: room.name,
        enquiry_type: "Room Booking",
        selected_offer: `${room.name} availability request`,
        message: form.message,
      });
      toast.success("Preferred-date enquiry submitted. Raaj Resort can contact you to confirm availability.");
      setForm({ check_in_date: "", check_out_date: "", number_of_guests: "", full_name: "", mobile_number: "", message: "" });
      setOpen(false);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail[0]?.msg || "Please check the availability request." : "Please check the availability request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="availability-panel" data-testid={`availability-panel-${room.slug}`}>
      <button type="button" className="availability-toggle" onClick={() => setOpen((value) => !value)} data-testid={`availability-toggle-${room.slug}`}>
        <CalendarDays size={17} /> Preferred-Date Enquiry
      </button>
      {open && (
        <form className="availability-form" onSubmit={submit} data-testid={`availability-form-${room.slug}`}>
          <div className="availability-grid">
            <label>Check-in / preferred date<input type="date" value={form.check_in_date} onChange={(e) => update("check_in_date", e.target.value)} required data-testid={`availability-check-in-${room.slug}`} /></label>
            <label>Check-out / preferred date<input type="date" value={form.check_out_date} onChange={(e) => update("check_out_date", e.target.value)} required data-testid={`availability-check-out-${room.slug}`} /></label>
            <label>Number of guests<input type="number" min="1" value={form.number_of_guests} onChange={(e) => update("number_of_guests", e.target.value)} required data-testid={`availability-guests-${room.slug}`} /></label>
            <label>Room type<input value={room.name} readOnly data-testid={`availability-room-type-${room.slug}`} /></label>
            <label>Name<input value={form.full_name} onChange={(e) => update("full_name", e.target.value)} required minLength={2} data-testid={`availability-name-${room.slug}`} /></label>
            <label>Phone<input value={form.mobile_number} onChange={(e) => update("mobile_number", e.target.value)} required data-testid={`availability-phone-${room.slug}`} /></label>
          </div>
          <label>Message<textarea rows={3} value={form.message} onChange={(e) => update("message", e.target.value)} required data-testid={`availability-message-${room.slug}`} /></label>
          <p>This creates an enquiry only. It does not confirm room availability or a booking automatically.</p>
          <button className="btn btn-primary full" disabled={submitting} type="submit" data-testid={`availability-submit-${room.slug}`}>{submitting ? "Submitting..." : "Submit Preferred-Date Enquiry"}</button>
        </form>
      )}
    </div>
  );
}

function RoomCard({ room }) {
  const [activeImage, setActiveImage] = useState(0);
  const images = room.gallery?.length ? room.gallery : [room.image];

  return (
    <motion.article whileHover={{ y: -4 }} className="room-card" key={room.name} data-testid={`room-card-${room.slug}`}>
      <div className="room-image-wrap premium-room-media">
        <img src={images[activeImage]} alt={`${room.name} photograph supplied by Raaj Resort`} data-testid={`room-main-image-${room.slug}`} />
        {room.features?.length > 0 && (
          <div className="room-feature-badges" data-testid={`room-feature-badges-${room.slug}`}>
            {room.features.map((feature) => <span key={feature}>{feature}</span>)}
          </div>
        )}
      </div>
      <div className="room-image-caption" data-testid={`room-caption-${room.slug}`}>{room.photoNote}</div>
      <div className="room-body">
        <div className="room-title-row">
          <h3>{room.name}</h3>
          <span>{room.persons}</span>
        </div>
        {images.length > 1 && (
          <div className="room-thumbnails" data-testid={`room-gallery-thumbnails-${room.slug}`}>
            {images.map((image, index) => ({ image, index })).filter((item) => item.index !== activeImage).map(({ image, index }) => (
              <button type="button" key={`${room.slug}-${image}`} onClick={() => setActiveImage(index)} data-testid={`room-thumbnail-${room.slug}-${index}`}>
                <img src={image} alt={`${room.name} thumbnail ${index + 1}`} />
              </button>
            ))}
          </div>
        )}
        <div className="tariff-table" aria-label={`${room.name} tariff`}>
          <div><strong>Weekdays</strong><span>{room.prices.weekday}</span></div>
          <div><strong>Weekends</strong><span>{room.prices.weekend}</span></div>
          <div><strong>One Day For 6 Hrs – Weekdays</strong><span>{room.prices.sixWeekday}</span></div>
          <div><strong>One Day For 6 Hrs – Weekends</strong><span>{room.prices.sixWeekend}</span></div>
        </div>
        <PreferredRoomEnquiry room={room} />
        <a
          className="btn btn-primary full"
          href={whatsappLink(`Hello Raaj Resort, I would like to enquire about ${room.name}.`)}
          target="_blank"
          rel="noreferrer"
          data-testid={`room-enquire-${room.slug}-button`}
        >
          Enquire for this Room
        </a>
      </div>
    </motion.article>
  );
}

function Rooms() {
  return (
    <section id="rooms" className="section warm-section" data-testid="rooms-section">
      <div className="container">
        <SectionHeading eyebrow="Stay / Rooms" title="Room tariff information provided by Raaj Resort.">
          Enquire for your preferred room. A submitted enquiry is not a confirmed booking until the resort confirms availability directly.
        </SectionHeading>
        <div className="room-grid">
          {rooms.map((room) => <RoomCard room={room} key={room.name} />)}
        </div>
        <div className="notes-card" data-testid="tariff-notes-card">
          <h3>Room Tariff Notes</h3>
          <ul>
            {tariffNotes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Packages() {
  return (
    <section id="packages" className="section" data-testid="group-packages-section">
      <div className="container">
        <SectionHeading eyebrow="Group Packages" title="Per-person packages for group stays.">
          Package prices and inclusions are displayed exactly as supplied by Raaj Resort.
        </SectionHeading>
        <div className="package-grid">
          {packageData.map((pkg) => (
            <article className="package-card" key={pkg.id} data-testid={pkg.testId}>
              <div className="package-head">
                <span className="package-id">{pkg.id}</span>
                <div>
                  <h3>{pkg.title}</h3>
                  <p>{pkg.min}</p>
                </div>
              </div>
              <div className="price-line">{pkg.price}</div>
              <div className="package-columns">
                <div>
                  <h4>Includes</h4>
                  <ul>{pkg.includes.map((item, index) => <li key={`${pkg.id}-include-${index}`}>{item}</li>)}</ul>
                </div>
                <div>
                  <h4>Room allocation</h4>
                  <ul>{pkg.allocation.map((item) => <li key={item}>{item}</li>)}</ul>
                </div>
              </div>
              <a className="btn btn-primary full" href={whatsappLink(`Hello Raaj Resort, I would like to enquire about ${pkg.title}.`)} target="_blank" rel="noreferrer" data-testid={`package-${pkg.id}-whatsapp-button`}>
                Enquire for this Package
              </a>
            </article>
          ))}
        </div>
        <div className="meal-card" data-testid="meal-timings-row">
          <h3>Meal Timings</h3>
          <div className="meal-grid">
            {mealTimings.map(([label, time]) => (
              <div key={label}><strong>{label}</strong><span>{time}</span></div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ImportantInformation() {
  return (
    <section id="important-information" className="section important-section" data-testid="important-information-section">
      <div className="container">
        <SectionHeading eyebrow="Guest Information" title="Important Information">
          Please review the free access items, chargeable services and important resort notes before planning your visit or event.
        </SectionHeading>
        <div className="important-grid">
          <article className="important-card" data-testid="free-access-card">
            <div className="important-card-head">
              <CircleCheck size={24} />
              <h3>Free Access</h3>
            </div>
            <ul>{freeAccessItems.map((item) => <li key={item}>{item}</li>)}</ul>
          </article>
          <article className="important-card chargeable" data-testid="chargeable-card">
            <div className="important-card-head">
              <BadgeIndianRupee size={24} />
              <h3>Chargeable</h3>
            </div>
            <ul>{chargeableItems.map((item) => <li key={item}>{item}</li>)}</ul>
          </article>
          <article className="important-card notes" data-testid="important-notes-card">
            <div className="important-card-head">
              <Info size={24} />
              <h3>Important Notes</h3>
            </div>
            <ul>{importantNotes.map((item) => <li key={item}>{item}</li>)}</ul>
          </article>
        </div>
      </div>
    </section>
  );
}

function RainDance() {
  return (
    <section id="rain-dance" className="section rain-dance-section" data-testid="rain-dance-section">
      <div className="container rain-dance-grid">
        <div>
          <div className="chargeable-badge" data-testid="rain-dance-chargeable-badge"><Waves size={18} /> Chargeable</div>
          <SectionHeading eyebrow="Chargeable Activity" title="Rain Dance">
            Rain Dance is available as a chargeable activity. Please contact Raaj Resort directly for current availability and details.
          </SectionHeading>
          <button className="btn btn-primary" onClick={() => scrollToSection("contact")} data-testid="rain-dance-enquiry-button">
            Enquire About Rain Dance
          </button>
        </div>
        <div className="rain-dance-gallery" data-testid="rain-dance-gallery">
          {rainDancePhotos.map((photo, index) => (
            <figure key={photo.src} data-testid={`rain-dance-photo-${index}`}>
              <img src={photo.src} alt={photo.alt} loading="lazy" />
              <figcaption>Rain Dance <span>Chargeable</span></figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}


function Weddings() {
  const facilities = [
    { name: "Wedding Lawn", slug: "wedding-lawn", image: ASSETS.weddingLawn, noCrop: true },
    { name: "A/C Banquet Hall", slug: "ac-banquet-hall", image: ASSETS.banquetHall },
    { name: "Kids Play Area", slug: "kids-play-area", image: ASSETS.kidsPlayArea },
    { name: "Spa & Wellness", slug: "spa-wellness", image: ASSETS.spaOne },
  ];
  return (
    <section id="weddings" className="section dark-band" data-testid="weddings-events-section">
      <div className="container">
        <div className="event-showcase no-photo-panel">
          <div>
            <SectionHeading eyebrow="Weddings & Events" title="A Perfect Setting for Weddings & Celebrations">
              From intimate family celebrations to weddings and special events, Raaj Resort offers a comfortable setting with a Wedding Lawn, A/C Banquet Hall, rooms and Pavika Restaurant & Bar.
            </SectionHeading>
            <div className="event-rating-card" data-testid="events-rating-card">
              <strong>3-Star Resort • 4.7 Google Rating</strong>
              <span>Ideal for weddings, celebrations and events near Panvel.</span>
            </div>
            <button className="btn btn-accent" onClick={() => scrollToSection("contact")} data-testid="weddings-enquiry-button">
              Plan Your Event
            </button>
          </div>
        </div>
        <div className="event-grid featured-events">
          {facilities.map((item) => (
            <article className={`event-tile with-photo ${item.noCrop ? "no-crop" : ""}`} key={item.name} data-testid={`wedding-space-tile-${item.slug}`}>
              <img src={item.image} alt={`${item.name} photograph supplied by Raaj Resort`} />
              <div className="event-label-panel">
                <Sparkles size={22} />
                <h3>{item.name}</h3>
              </div>
            </article>
          ))}
        </div>
        <div className="wedding-lawn-views" data-testid="wedding-lawn-photo-gallery">
          {[ASSETS.weddingLawnAerial].map((image, index) => (
            <figure key={image}>
              <img src={image} alt={`Wedding Lawn photograph ${index + 1} supplied by Raaj Resort`} />
              <figcaption>Wedding Lawn</figcaption>
            </figure>
          ))}
        </div>
        <div className="wedding-decoration-gallery" data-testid="wedding-decoration-gallery">
          <div className="wedding-decoration-heading">
            <p className="eyebrow">Wedding Decoration</p>
            <h3>Wedding Decoration</h3>
          </div>
          <div className="wedding-decoration-grid">
            {weddingDecorationPhotos.map((photo, index) => (
              <figure key={photo.src} data-testid={`wedding-decoration-photo-${index}`}>
                <img src={photo.src} alt={photo.alt} loading="lazy" />
                <figcaption>Wedding Decoration</figcaption>
              </figure>
            ))}
          </div>
        </div>


      </div>
    </section>
  );
}

function CaterersBuffetSetup() {
  return (
    <section id="caterers-buffet-setup" className="section caterers-section" data-testid="caterers-buffet-setup-section">
      <div className="container">
        <SectionHeading eyebrow="Events & Dining Setup" title="Caterers & Buffet Setup">
          View the supplied Caterers & Buffet Setup photographs for event and function arrangements at Raaj Resort.
        </SectionHeading>
        <div className="caterers-gallery" data-testid="caterers-buffet-setup-gallery">
          {caterersBuffetPhotos.map((photo, index) => (
            <figure key={photo.src} data-testid={`caterers-buffet-setup-photo-${index}`}>
              <img src={photo.src} alt={photo.alt} loading="lazy" />
              <figcaption>Caterers & Buffet Setup</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function WeddingPackage() {
  return (
    <section id="wedding-package" className="section wedding-package-section" data-testid="wedding-package-section">
      <div className="container wedding-package-grid">
        <figure className="wedding-package-poster" data-testid="wedding-package-poster">
          <img src={ASSETS.weddingPackagePoster} alt="Wedding Package poster supplied by Raaj Resort" loading="lazy" />
          <figcaption>Wedding Package</figcaption>
        </figure>
        <div className="wedding-package-copy">
          <SectionHeading eyebrow="Wedding Package" title="Perfect Wedding Destination at Raaj Resort">
            Accommodation facilities are available for weddings, with venue, lawn, parking and event services information shown exactly from the supplied poster details.
          </SectionHeading>
          <div className="wedding-package-list" data-testid="wedding-package-info-list">
            {weddingPackageInfo.map((item) => (
              <div key={item}>
                <CircleCheck size={17} />
                <span>{item}</span>
              </div>
            ))}
          </div>
          <a className="btn btn-accent" href={whatsappLink("Hello Raaj Resort, I would like to enquire about the Wedding Package.")} target="_blank" rel="noreferrer" data-testid="wedding-package-whatsapp-button">
            Enquire on WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
}

function ParkingArea() {
  return (
    <section id="parking-area" className="section parking-section" data-testid="parking-area-section">
      <div className="container parking-grid">
        <div>
          <SectionHeading eyebrow="Guest Convenience" title="Parking Area">
            The Parking Area photograph shown here is the uploaded image supplied for this section only.
          </SectionHeading>
        </div>
        <div className="parking-gallery" data-testid="parking-area-gallery">
          {parkingAreaPhotos.map((photo, index) => (
            <figure key={photo.src} data-testid={`parking-area-photo-${index}`}>
              <img src={photo.src} alt={photo.alt} loading="lazy" />
              <figcaption>Parking Area</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}



function SpaWellness() {
  const [activeSpa, setActiveSpa] = useState(0);
  const activePhoto = spaPhotos[activeSpa];
  const moveSpa = (direction) => setActiveSpa((index) => (index + direction + spaPhotos.length) % spaPhotos.length);

  return (
    <section id="spa" className="section spa-section" data-testid="spa-wellness-section">
      <div className="container spa-grid">
        <div>
          <SectionHeading eyebrow="Spa & Wellness" title="A calmer pause within the Raaj Resort experience.">
            Spa & Wellness is shown using the actual spa photographs supplied by Raaj Resort. Please contact the resort directly for current availability and details.
          </SectionHeading>
          <button className="btn btn-primary" onClick={() => scrollToSection("contact")} data-testid="spa-enquiry-button">
            Enquire About Spa & Wellness
          </button>
        </div>
        <div className="spa-gallery-card" data-testid="spa-photo-gallery">
          <figure className="spa-photo-card">
            <img src={activePhoto.src} alt={activePhoto.alt} />
            <figcaption>Spa & Wellness</figcaption>
          </figure>
          <div className="spa-gallery-controls">
            <button type="button" onClick={() => moveSpa(-1)} aria-label="Previous spa photograph" data-testid="spa-gallery-prev-button"><ChevronLeft size={18} /></button>
            <span>{activeSpa + 1} / {spaPhotos.length}</span>
            <button type="button" onClick={() => moveSpa(1)} aria-label="Next spa photograph" data-testid="spa-gallery-next-button"><ChevronRight size={18} /></button>
          </div>
          <div className="spa-thumbnails" aria-label="Spa & Wellness photographs">
            {spaPhotos.map((photo, index) => ({ photo, index })).filter((item) => item.index !== activeSpa).map(({ photo, index }) => (
              <button type="button" key={photo.src} onClick={() => setActiveSpa(index)} data-testid={`spa-thumbnail-${index}`}>
                <img src={photo.src} alt={photo.alt} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Restaurant() {
  return (
    <section id="restaurant" className="section" data-testid="restaurant-section">
      <div className="container restaurant-grid">
        <div>
          <SectionHeading eyebrow="Restaurant" title="Pavika Restaurant & Bar">
            For restaurant and dining enquiries, contact Raaj Resort directly. Menu details are not listed here because no menu information was supplied for this version.
          </SectionHeading>
          <button className="btn btn-primary" onClick={() => scrollToSection("contact")} data-testid="restaurant-enquiry-button">
            Enquire / Contact Resort
          </button>
        </div>
        <img src={ASSETS.restaurantDining} alt="Pavika Restaurant & Bar dining space at Raaj Resort" />
      </div>
    </section>
  );
}

function Gallery() {
  const [current, setCurrent] = useState(null);
  const move = (dir) => setCurrent((idx) => (idx + dir + galleryImages.length) % galleryImages.length);

  useEffect(() => {
    if (current === null) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setCurrent(null);
      if (event.key === "ArrowRight") move(1);
      if (event.key === "ArrowLeft") move(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [current]);

  return (
    <section id="gallery" className="section warm-section" data-testid="gallery-section">
      <div className="container">
        <SectionHeading eyebrow="Gallery" title="Actual Raaj Resort photographs.">
          View supplied photographs of the Wedding Lawn, A/C Banquet Hall, Kids Play Area, Spa & Wellness, rooms, dining spaces and resort experience.
        </SectionHeading>
        <div className="gallery-grid">
          {galleryImages.map((image, index) => (
            <button key={image.src} className="gallery-item" onClick={() => setCurrent(index)} data-testid={`gallery-image-${index}`}>
              <img src={image.src} alt={image.title} />
              <span>{image.category}</span>
            </button>
          ))}
        </div>
      </div>
      {current !== null && (
        <div className="lightbox" role="dialog" aria-modal="true" data-testid="gallery-lightbox-dialog">
          <button className="lightbox-close" onClick={() => setCurrent(null)} aria-label="Close gallery" data-testid="gallery-lightbox-close-button"><X /></button>
          <button className="lightbox-arrow left" onClick={() => move(-1)} aria-label="Previous image" data-testid="gallery-lightbox-prev-button"><ChevronLeft /></button>
          <figure>
            <img src={galleryImages[current].src} alt={galleryImages[current].title} />
            <figcaption>{galleryImages[current].title}</figcaption>
          </figure>
          <button className="lightbox-arrow right" onClick={() => move(1)} aria-label="Next image" data-testid="gallery-lightbox-next-button"><ChevronRight /></button>
        </div>
      )}
    </section>
  );
}

const emptyForm = {
  full_name: "",
  mobile_number: "",
  email: "",
  check_in_date: "",
  check_out_date: "",
  number_of_guests: "",
  room_type: "Not decided",
  enquiry_type: "Room Booking",
  event_type: "",
  selected_offer: "",
  message: "",
};

function EnquiryForm({ compact = false }) {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, number_of_guests: Number(form.number_of_guests), event_type: form.event_type || null };
      await axios.post(`${API}/enquiries`, payload);
      toast.success("Enquiry submitted. Raaj Resort can contact you to confirm details.");
      setForm(emptyForm);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail[0]?.msg || "Please check the form details." : "Please check the form details.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={`enquiry-form ${compact ? "compact" : ""}`} onSubmit={submit} data-testid="enquiry-form">
      <div className="form-grid">
        <label>Full Name<input data-testid="enquiry-full-name-input" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} required minLength={2} /></label>
        <label>Mobile Number<input data-testid="enquiry-mobile-number-input" value={form.mobile_number} onChange={(e) => update("mobile_number", e.target.value)} required /></label>
        <label>Email<input data-testid="enquiry-email-input" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required /></label>
        <label>Check-in Date<input data-testid="enquiry-check-in-date-input" type="date" value={form.check_in_date} onChange={(e) => update("check_in_date", e.target.value)} required /></label>
        <label>Check-out Date<input data-testid="enquiry-check-out-date-input" type="date" value={form.check_out_date} onChange={(e) => update("check_out_date", e.target.value)} required /></label>
        <label>Number of Guests<input data-testid="enquiry-guests-input" type="number" min="1" value={form.number_of_guests} onChange={(e) => update("number_of_guests", e.target.value)} required /></label>
        <label>Room Type<select data-testid="enquiry-room-type-select" value={form.room_type} onChange={(e) => update("room_type", e.target.value)} required>{roomTypes.map((type) => <option key={type}>{type}</option>)}</select></label>
        <label>Enquiry Type<select data-testid="enquiry-type-select" value={form.enquiry_type} onChange={(e) => update("enquiry_type", e.target.value)} required>{enquiryTypes.map((type) => <option key={type}>{type}</option>)}</select></label>
        <label>Event Type<select data-testid="event-type-select" value={form.event_type} onChange={(e) => update("event_type", e.target.value)}>{["", ...eventTypes].map((type) => <option key={type} value={type}>{type || "Select if applicable"}</option>)}</select></label>
      </div>
      <label>Message<textarea data-testid="enquiry-message-textarea" value={form.message} onChange={(e) => update("message", e.target.value)} rows={4} /></label>
      <p className="form-note">Submitting an enquiry does not confirm a booking. The resort will confirm availability directly.</p>
      <button className="btn btn-primary full" disabled={submitting} type="submit" data-testid="enquiry-form-submit-button">
        {submitting ? "Submitting..." : "Submit Enquiry"}
      </button>
    </form>
  );
}

function Contact() {
  return (
    <section id="contact" className="section contact-section" data-testid="contact-section">
      <div className="container contact-grid">
        <div className="contact-card">
          <SectionHeading eyebrow="Contact" title="Raaj Resort" />
          <div className="contact-list">
            <p><MapPin size={18} /> <span>{BUSINESS.address}</span></p>
            <a href="tel:+919819344222" data-testid="contact-phone-9819344222"><Phone size={18} /> +91 98193 44222</a>
            <a href={`mailto:${BUSINESS.email}`} data-testid="contact-email-link"><Mail size={18} /> {BUSINESS.email}</a>
          </div>
          <div className="contact-actions">
            <a className="btn btn-primary" href="tel:+919819344222" data-testid="contact-call-now-link">Call Now</a>
            <a className="btn btn-accent" href={whatsappLink()} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link">WhatsApp</a>
            <a className="btn btn-light" href={`mailto:${BUSINESS.email}`} data-testid="contact-email-action-link">Email</a>
            <a className="btn btn-light" href={directionsUrl} target="_blank" rel="noreferrer" data-testid="contact-get-directions-link">Get Directions <ExternalLink size={15} /></a>
          </div>
        </div>
        <div className="form-card">
          <h3>Book / Enquire Now</h3>
          <EnquiryForm />
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer" data-testid="site-footer">
      <div className="container footer-grid">
        <div>
          <img src={ASSETS.logo} alt="Raaj Resort official logo" />
          <p>Raaj Resort near Panvel for stays, groups, weddings, events and Pavika Restaurant & Bar enquiries.</p>
        </div>
        <div>
          <h4>Quick Links</h4>
          {navLinks.slice(1).map(([label, id]) => <button key={id} onClick={() => scrollToSection(id)}>{label}</button>)}
        </div>
        <div>
          <h4>Contact</h4>
          <p>{BUSINESS.phones.join(" / ")}</p>
          <p>{BUSINESS.email}</p>
          <p>{BUSINESS.address}</p>
          <a href={whatsappLink()} target="_blank" rel="noreferrer">WhatsApp</a>
        </div>
      </div>
    </footer>
  );
}

function MobileStickyCta() {
  return (
    <div className="mobile-cta-bar" data-testid="mobile-floating-contact-bar">
      <a href={whatsappLink()} target="_blank" rel="noreferrer" data-testid="mobile-whatsapp-button">WhatsApp</a>
      <a href="tel:+919819344222" data-testid="mobile-call-button">Call</a>
    </div>
  );
}

function HomePage() {
  useEffect(() => {
    document.title = "Raaj Resort | Rooms, Group Packages, Weddings & Events Near Panvel";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Discover Raaj Resort near Panvel, a 3-star property with a 4.7 Google Rating, offering rooms, group packages, Wedding Lawn, A/C Banquet Hall and Pavika Restaurant & Bar. Enquire for your stay or event.");
  }, []);

  return (
    <div>
      <Navigation />
      <Hero />
      <About />
      <Rooms />
      <Packages />
      <ImportantInformation />
      <RainDance />
      <Weddings />
      <WeddingPackage />
      <CaterersBuffetSetup />
      <ParkingArea />
      <SpaWellness />
      <Restaurant />
      <Gallery />
      <Contact />
      <Footer />
      <MobileStickyCta />
    </div>
  );
}

function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("raaj_admin_token") || "");
  const [login, setLogin] = useState({ email: "", password: "" });
  const [enquiries, setEnquiries] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadEnquiries = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, { headers, params: { search: search || undefined, status: status || undefined, enquiry_type: typeFilter || undefined } });
      setEnquiries(data);
    } catch (error) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        localStorage.removeItem("raaj_admin_token");
        setToken("");
      }
      toast.error("Unable to load enquiries.");
    } finally {
      setLoading(false);
    }
  }, [headers, search, status, token, typeFilter]);

  useEffect(() => {
    loadEnquiries();
  }, [loadEnquiries]);

  const loginAdmin = async (event) => {
    event.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/login`, login);
      localStorage.setItem("raaj_admin_token", data.token);
      setToken(data.token);
      toast.success("Admin login successful.");
    } catch {
      toast.error("Invalid admin credentials.");
    }
  };

  const updateEnquiry = async (id, payload) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${id}`, payload, { headers });
      toast.success("Enquiry updated.");
      loadEnquiries();
    } catch {
      toast.error("Could not update enquiry.");
    }
  };

  const exportCsv = async () => {
    try {
      const response = await axios.get(`${API}/admin/enquiries/export`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "raaj-resort-enquiries.csv";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export CSV.");
    }
  };

  if (!token) {
    return (
      <main className="admin-shell login-shell">
        <button className="btn btn-light" onClick={() => navigate("/")} data-testid="admin-back-home-button">Back to Website</button>
        <form className="admin-login" onSubmit={loginAdmin} data-testid="admin-login-form">
          <ShieldCheck size={32} />
          <h1>Raaj Resort Admin</h1>
          <p>Private lead dashboard for resort enquiries.</p>
          <label>Email<input data-testid="admin-email-input" type="email" value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} required /></label>
          <label>Password<input data-testid="admin-password-input" type="password" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} required /></label>
          <button className="btn btn-primary full" type="submit" data-testid="admin-login-submit-button">Login</button>
        </form>
      </main>
    );
  }

  return (
    <main className="admin-shell" data-testid="admin-dashboard">
      <div className="admin-topbar">
        <div>
          <p className="eyebrow">Private dashboard</p>
          <h1>Raaj Resort Enquiries</h1>
        </div>
        <div className="admin-top-actions">
          <button className="btn btn-light" onClick={() => navigate("/")} data-testid="admin-view-website-button">Website</button>
          <button className="btn btn-light" onClick={() => { localStorage.removeItem("raaj_admin_token"); setToken(""); }} data-testid="admin-logout-button">Logout</button>
        </div>
      </div>
      <div className="admin-filters">
        <label className="search-box"><Search size={16} /><input data-testid="admin-enquiries-search-input" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadEnquiries()} /></label>
        <select data-testid="admin-enquiries-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">All statuses</option>{statuses.map((s) => <option key={s}>{s}</option>)}</select>
        <select data-testid="admin-enquiries-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}><option value="">All enquiry types</option>{enquiryTypes.map((s) => <option key={s}>{s}</option>)}</select>
        <button className="btn btn-primary" onClick={loadEnquiries} data-testid="admin-search-button">Search</button>
        <button className="btn btn-accent" onClick={exportCsv} data-testid="admin-enquiries-export-csv-button"><Download size={16} /> Export CSV</button>
      </div>
      <div className="admin-table-wrap">
        {loading ? <p>Loading enquiries...</p> : (
          <table className="admin-table" data-testid="admin-enquiries-table">
            <thead><tr><th>Guest</th><th>Details</th><th>Enquiry</th><th>Status</th><th>Internal Notes</th><th>Update</th></tr></thead>
            <tbody>
              {enquiries.map((item) => <AdminRow key={item.id} item={item} onUpdate={updateEnquiry} />)}
              {enquiries.length === 0 && <tr><td colSpan="6">No enquiries found.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </main>
  );
}

function AdminRow({ item, onUpdate }) {
  const [status, setStatus] = useState(item.status);
  const [notes, setNotes] = useState(item.internal_notes || "");
  return (
    <tr data-testid={`admin-enquiry-row-${item.id}`}>
      <td><strong>{item.full_name}</strong><span>{item.mobile_number}</span><span>{item.email}</span></td>
      <td><span>{item.check_in_date || "No check-in date"} to {item.check_out_date || "No check-out date"}</span><span>{item.number_of_guests} guests</span><span>{item.room_type}</span></td>
      <td><strong>{item.enquiry_type}</strong><span>{item.event_type || item.selected_offer || "General"}</span><span>{item.message || "No message added"}</span></td>
      <td><select value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`admin-enquiry-status-${item.id}`}>{statuses.map((s) => <option key={s}>{s}</option>)}</select></td>
      <td><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid={`admin-enquiry-notes-${item.id}`} /></td>
      <td><button className="btn btn-primary" onClick={() => onUpdate(item.id, { status, internal_notes: notes })} data-testid={`admin-enquiry-update-button-${item.id}`}>Save</button></td>
    </tr>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
